import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { HuddlePost } from '../types';

const TOPIC_COLORS: Record<string, string> = {
  identity:   '#014c8d',
  transition: '#bf1e2e',
  wealth:     '#0fa86d',
  leadership: '#e88c0e',
  legacy:     '#6b46c1',
  general:    '#4a6080',
};

function topicColor(topicId: string | null): string {
  const key = (topicId || '').toLowerCase();
  for (const k of Object.keys(TOPIC_COLORS)) {
    if (key.includes(k)) return TOPIC_COLORS[k];
  }
  return TOPIC_COLORS.general;
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function initials(name: string): string {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function PostCard({ post, isReply = false }: { post: HuddlePost; isReply?: boolean }) {
  const color = topicColor(post.topic_id);
  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)',
      padding: '14px 16px', marginBottom: 10,
      borderLeftWidth: 3, borderLeftColor: color, borderLeftStyle: 'solid',
      marginLeft: isReply ? 28 : 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10, color: '#fff', flexShrink: 0 }}>
          {initials(post.user_name || 'BA')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{post.user_name || 'Anonymous'}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{formatDate(post.created_at)}</div>
        </div>
        {post.topic_id && (
          <span style={{ padding: '2px 7px', borderRadius: 10, background: color + '22', color: color, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
            {post.topic_id}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-3)' }}>
          ♥ {post.likes_count || 0}
        </div>
      </div>

      {post.title && !isReply && (
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, color: 'var(--text)', marginBottom: 6, lineHeight: 1.2 }}>{post.title}</div>
      )}
      {post.subtitle && !isReply && (
        <div style={{ fontSize: 12, color: '#e88c0e', fontWeight: 600, marginBottom: 6 }}>{post.subtitle}</div>
      )}
      {post.content && (
        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>{post.content}</div>
      )}
    </div>
  );
}

export default function HuddlePage() {
  const [posts, setPosts] = useState<HuddlePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'posts' | 'stats'>('posts');

  useEffect(() => {
    supabase
      .from('huddle_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setPosts((data as HuddlePost[]) ?? []);
        setLoading(false);
      });
  }, []);

  // Compute topic counts
  const topicCounts: Record<string, number> = {};
  posts.forEach(p => {
    const key = p.topic_id || 'general';
    topicCounts[key] = (topicCounts[key] || 0) + 1;
  });

  const topLevelPosts = posts.filter(p => !p.parent_id);
  const replies = posts.filter(p => p.parent_id);
  const totalLikes = posts.reduce((s, p) => s + (p.likes_count || 0), 0);

  const Tab = ({ id, label }: { id: 'posts' | 'stats'; label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={activeTab === id ? 'btn btn-danger btn-sm' : 'btn btn-ghost btn-sm'}
    >{label}</button>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>The Huddle</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>Community posts · {loading ? '...' : `${posts.length} total`}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Tab id="posts" label="Posts" />
          <Tab id="stats" label="Stats" />
        </div>
      </div>

      {activeTab === 'stats' && (
        <>
          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
            {[
              { label: 'Total Posts', value: posts.length, color: '#bf1e2e' },
              { label: 'Top-Level Posts', value: topLevelPosts.length, color: '#014c8d' },
              { label: 'Replies', value: replies.length, color: '#0fa86d' },
              { label: 'Total Likes', value: totalLikes, color: '#e88c0e' },
            ].map(s => (
              <div key={s.label} className="panel" style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.color }} />
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 32, color: 'var(--text)', lineHeight: 1 }}>{loading ? '—' : s.value}</div>
                <div className="label-caps" style={{ marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Topic breakdown */}
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Posts by Topic</div></div>
            <div style={{ padding: '16px 18px' }}>
              {loading ? (
                <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '20px 0' }}>Loading...</div>
              ) : Object.keys(topicCounts).length === 0 ? (
                <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '20px 0' }}>No posts yet</div>
              ) : (
                Object.entries(topicCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([topic, count]) => {
                    const color = topicColor(topic);
                    const max = Math.max(...Object.values(topicCounts));
                    return (
                      <div key={topic} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', width: 120, flexShrink: 0, textTransform: 'capitalize' }}>{topic}</div>
                        <div style={{ flex: 1, height: 7, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(count / max) * 100}%`, background: color, borderRadius: 10 }} />
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', width: 24, textAlign: 'right' }}>{count}</div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'posts' && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading posts...</div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>No posts in The Huddle yet.</div>
          ) : (
            <div style={{ maxWidth: 680 }}>
              {topLevelPosts.map(post => {
                const postReplies = replies.filter(r => r.parent_id === post.id);
                return (
                  <div key={post.id}>
                    <PostCard post={post} />
                    {postReplies.map(reply => <PostCard key={reply.id} post={reply} isReply />)}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
